import React, { useEffect, useId, useRef, useState } from 'react';
import { FaArrowLeft, FaLock, FaTimes } from 'react-icons/fa';
import { useLocale } from '../../../contexts/LocaleContext.jsx';
import { useFetchWrapper } from '../../../_helpers/fetchWrapper.js';
import { useRequestQuote } from '../../../hooks/useRequestQuote.js';
import config from '../../../config.js';
import '../../../styles/SongCratePanel.css';
import '../../../styles/SongPagePanel.css';
import '../../../styles/StreamCta.css';

/** Errors with their own message on the check step; anything else is the generic one. */
const CHECK_ERRORS = {
  card_declined: 'stream.check.errors.cardDeclined',
  free_requests_disabled: 'stream.check.errors.freeOff',
  song_played_this_stream: 'stream.check.errors.playedTonight',
  request_limit_reached: 'stream.check.errors.limit',
  queue_full: 'stream.check.errors.queueFull',
  requests_closed: 'stream.check.errors.closed',
  show_not_live: 'stream.check.errors.closed',
  song_not_requestable: 'stream.check.errors.notRequestable',
  request_refunded: 'stream.check.errors.refunded',
  request_pending: 'stream.check.errors.pending',
};

/**
 * The check step of a request (mockups 23, 24 and 27; ENG-711/713/719): what
 * the listener gives, where the song lands — re-quoted whenever the queue
 * changes (`queueKey`) — and how they pay, then Send.
 *
 * - Paid, with a saved card (`GET /me/payment-method`): PAY WITH the card,
 *   charged in one tap; CHANGE pays with a new card instead.
 * - Paid, with no saved card (or after CHANGE): the card is entered on
 *   Stripe's own page (Checkout, ENG-714), so card data goes straight to
 *   Stripe; "Save this card for next time" keeps it for one tap later.
 * - Free: no payment rows; lands at the end of the line.
 *
 * Change amount goes back to the song page with the choice kept. The
 * payment hook (`payment`, useRequestPayment) sends one request per click.
 */
const RequestCheckPanel = ({ bandId, song, choice, queueKey, payment, onChangeAmount, onClose }) => {
  const { t, format } = useLocale();
  const fetchWrapperRef = useRef(useFetchWrapper());
  const headingId = useId();
  const nameId = useId();
  const saveId = useId();
  const isFree = Boolean(choice?.free);
  const [card, setCard] = useState(null);
  const [cardLoaded, setCardLoaded] = useState(isFree);
  const [useNewCard, setUseNewCard] = useState(false);
  const [saveCard, setSaveCard] = useState(true);
  const [name, setName] = useState('');
  const { quote } = useRequestQuote(bandId, song.id, isFree ? {} : { amount: choice.amount }, queueKey);

  useEffect(() => {
    if (isFree) return undefined;
    let cancelled = false;
    fetchWrapperRef.current
      .get(`${config.apiBaseUrl}/me/payment-method`)
      .then((answer) => { if (!cancelled) setCard(answer?.hasSavedCard ? answer : null); })
      .catch(() => { if (!cancelled) setCard(null); })
      .finally(() => { if (!cancelled) setCardLoaded(true); });
    return () => { cancelled = true; };
  }, [isFree]);

  const usd = (value) => format.usd(value, { cents: 'always' });
  const lands = isFree ? quote?.freeSpot ?? choice.spot : quote?.targetSpot ?? choice.spot;
  const paysWithSaved = !isFree && card && !useNewCard;
  const busy = payment.state === 'sending' || payment.state === 'redirecting';

  const send = () => {
    if (busy) return;
    if (isFree) {
      payment.free(song, name.trim());
      return;
    }
    payment.pay(song, choice.amount, { requestedBy: name.trim(), card: paysWithSaved ? card : null, saveCard });
  };

  const errorText = payment.error
    ? (CHECK_ERRORS[payment.error] ? t(CHECK_ERRORS[payment.error]) : (/^[a-z_]+$/.test(payment.error) ? t('stream.check.errors.generic') : payment.error))
    : null;

  return (
    <section className="crate-panel song-check" aria-labelledby={headingId} data-testid="request-check">
      <header className="crate-panel-header">
        <div>
          <span className="crate-panel-kicker">{t('stream.check.kicker')}</span>
          <h2 id={headingId} className="crate-panel-title">{song.title}</h2>
        </div>
        <button type="button" className="crate-panel-close" onClick={onClose} aria-label={t('stream.crate.panel.close')}>
          <FaTimes aria-hidden="true" />
        </button>
      </header>

      <dl className="song-check-rows">
        <div className="song-check-row">
          <dt>{t('stream.check.donation')}</dt>
          <dd data-testid="request-check-amount">{isFree ? t('stream.check.free') : usd(choice.amount)}</dd>
        </div>
        <div className="song-check-row">
          <dt>{t('stream.check.landsAt')}</dt>
          <dd data-testid="request-check-spot">
            {lands != null ? t(isFree ? 'stream.check.spotEnd' : 'stream.check.spot', { spot: format.number(lands) }) : '–'}
          </dd>
        </div>
        {!isFree && cardLoaded && paysWithSaved && (
          <div className="song-check-row">
            <dt>{t('stream.check.payWith')}</dt>
            <dd>
              <span data-testid="request-check-card">{t('stream.check.card', { brand: (card.brand || '').toUpperCase(), last4: card.last4 || '' })}</span>
              <button type="button" className="crate-panel-link" onClick={() => setUseNewCard(true)}>{t('stream.check.change')}</button>
            </dd>
          </div>
        )}
      </dl>

      {!isFree && cardLoaded && !paysWithSaved && (
        <div className="song-check-newcard" data-testid="request-check-newcard">
          <p className="song-check-stripe"><FaLock aria-hidden="true" /> {t('stream.check.newCard')}</p>
          <label htmlFor={saveId} className="song-check-save">
            <input id={saveId} type="checkbox" checked={saveCard} onChange={(e) => setSaveCard(e.target.checked)} />
            {t('stream.check.saveCard')}
          </label>
        </div>
      )}

      <label htmlFor={nameId} className="crate-confirm-label">{t('stream.crate.panel.confirm.nameLabel')}</label>
      <input id={nameId} className="crate-confirm-name" value={name} maxLength={50} onChange={(e) => setName(e.target.value)} />

      {errorText && <p className="crate-confirm-error" role="alert">{errorText}</p>}

      <button
        type="button"
        className="boost-desk-go song-page-go stream-cta"
        disabled={busy || (!isFree && !cardLoaded)}
        aria-busy={busy || undefined}
        onClick={send}
        data-testid="request-check-send"
      >
        <span className="boost-desk-go-label">
          {busy
            ? t(payment.state === 'redirecting' ? 'stream.check.redirecting' : 'stream.check.sending')
            : isFree ? t('stream.check.sendFree') : t('stream.check.sendPaid', { amount: format.usd(choice.amount, { cents: 'auto' }) })}
        </span>
      </button>
      <button type="button" className="song-page-back" onClick={onChangeAmount}>
        <FaArrowLeft className="rtl-mirror" aria-hidden="true" /> {t('stream.check.changeAmount')}
      </button>
    </section>
  );
};

export default RequestCheckPanel;
